import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import api from '../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../Component/server';
import LiveDetailDialog from '../LiveList/Component/LiveDetailDialog';
import { AdminActionButton } from '../../../Component/UI/AdminPageHeader';
import { BankExcelMapping, BankExcelTemplate, SellerBankAccount } from '../../../Component/Interface/Store';

type AccountDraft = Pick<SellerBankAccount, 'label' | 'bank_name' | 'bank_num' | 'depositor' | 'active' | 'excel_template_seq'> & { seq?: number };
const accountValues = (account: SellerBankAccount): AccountDraft => ({ seq: account.seq, label: account.label, bank_name: account.bank_name, bank_num: account.bank_num, depositor: account.depositor, active: account.active, excel_template_seq: account.excel_template_seq ?? null });
type TemplateDraft = { seq?: number; name: string; mapping: BankExcelMapping | null };
type SampleSheet = { name: string; rows: unknown[][] };
const emptyAccount = (): AccountDraft => ({ label: '', bank_name: '', bank_num: '', depositor: '', excel_template_seq: null, active: true });
const emptyTemplate = (): TemplateDraft => ({ name: '', mapping: null });
const jsonConfig = { headers: { 'Content-Type': 'application/json' } };
const mappingFields = [
  { key: 'depositor_column', label: '입금자명 열' },
  { key: 'amount_column', label: '입금액 열' },
] as const;
const errorText = (cause: any, fallback: string) => {
  const message = cause?.response?.data?.message;
  return Array.isArray(message) ? message.join(' ') : typeof message === 'string' ? message : fallback;
};

type Props = {
  disabled?: boolean;
  selection?: { selectedSeq?: number | null; onSelect: (account: SellerBankAccount) => void; onClose: () => void; onAccountsChange: (accounts: SellerBankAccount[]) => void };
};

export default function BankAccounts({ disabled = false, selection }: Props) {
  const [managerOpen, setManagerOpen] = useState(false);
  const isOpen = !!selection || managerOpen;
  const [canManage, setCanManage] = useState(true);
  const [selectedSeq, setSelectedSeq] = useState(selection?.selectedSeq ?? null);
  const [page, setPage] = useState(1);
  const [linkAccount, setLinkAccount] = useState<SellerBankAccount | null>(null);
  const [linkTemplate, setLinkTemplate] = useState('');
  const [removeAccount, setRemoveAccount] = useState<SellerBankAccount | null>(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const [discard, setDiscard] = useState(false);
  const [accounts, setAccounts] = useState<SellerBankAccount[]>([]);
  const [templates, setTemplates] = useState<BankExcelTemplate[]>([]);
  const [accountDraft, setAccountDraft] = useState<AccountDraft | null>(null);
  const [templateDraft, setTemplateDraft] = useState<TemplateDraft | null>(null);
  const [sheets, setSheets] = useState<SampleSheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState<'save' | 'sample' | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const request = useRef(0);
  const mounted = useRef(false);
  const sampleInput = useRef<HTMLInputElement>(null);
  const initialDraft = useRef('');

  const load = useCallback(async () => {
    const current = ++request.current; setLoading(true); setLoadError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}store/bank-accounts`, { params: { purpose: selectionRef.current ? 'live' : 'settings' } });
      if (!mounted.current || current !== request.current) return false;
      if (!Array.isArray(data?.accounts) || !Array.isArray(data?.excel_templates)) throw new Error('Invalid bank settings');
      setAccounts(data.accounts); setTemplates(data.excel_templates); setCanManage(data.can_manage !== false);
      selectionRef.current?.onAccountsChange(data.accounts); return true;
    } catch (cause) {
      if (mounted.current && current === request.current) setLoadError(errorText(cause, '무통장 설정을 불러오지 못했습니다. 다시 시도해주세요.'));
      return false;
    } finally { if (mounted.current && current === request.current) setLoading(false); }
  }, []);
  useEffect(() => {
    mounted.current = true;
    if (isOpen) void load();
    return () => { mounted.current = false; request.current += 1; };
  }, [load, isOpen]);

  const draftSignature = (account: AccountDraft | null, template: TemplateDraft | null) => JSON.stringify({ account, template });
  const hasUnsavedDraft = () => Boolean(accountDraft || templateDraft) && draftSignature(accountDraft, templateDraft) !== initialDraft.current;
  const startAccountDraft = (draft: AccountDraft) => {
    initialDraft.current = draftSignature(draft, null); setAccountDraft(draft); setTemplateDraft(null); setDiscard(false); setError(''); setMessage('');
  };
  const startTemplateDraft = (draft: TemplateDraft) => {
    initialDraft.current = draftSignature(null, draft); setTemplateDraft(draft); setAccountDraft(null); setDiscard(false); setSheets([]); setError(''); setMessage('');
  };
  const resetEditor = () => {
    initialDraft.current = ''; setAccountDraft(null); setTemplateDraft(null); setDiscard(false); setSheets([]); setError('');
  };
  const closeEditor = () => {
    if (busy) return;
    if (hasUnsavedDraft()) { setDiscard(true); return; }
    resetEditor();
  };
  const visibleAccounts = selection ? accounts.filter(account => account.active) : accounts;
  const pages = Math.max(1, Math.ceil(visibleAccounts.length / 5));
  const currentPage = Math.min(page, pages);
  const selectedAccount = visibleAccounts.find(account => account.seq === selectedSeq);
  const editing = !!(accountDraft || templateDraft || linkAccount || removeAccount);
  const locked = disabled || !!busy || !canManage;
  const startLink = (account: SellerBankAccount) => { setLinkAccount(account); setLinkTemplate(String(account.excel_template_seq ?? '')); setError(''); setMessage(''); };
  const patchAccount = async (account: SellerBankAccount, fields: Partial<AccountDraft>) => {
    const { seq, ...values } = accountValues(account);
    await api.patch(`${SERVER_ADDRESS}store/bank-accounts/${seq}`, { ...values, ...fields }, jsonConfig);
  };
  const updateAccount = async (account: SellerBankAccount, fields: Partial<AccountDraft>) => {
    if (locked) return;
    setBusy('save'); setError(''); setMessage('');
    try {
      await patchAccount(account, fields);
      setLinkAccount(null); setRemoveAccount(null);
      if (fields.active === false && selectedSeq === account.seq) setSelectedSeq(null);
      await load(); setMessage('통장과 연결 엑셀을 저장했습니다.');
    } catch (cause) { setError(errorText(cause, '통장 설정을 저장하지 못했습니다. 다시 시도해주세요.')); }
    finally { setBusy(null); }
  };
  const mapping = templateDraft?.mapping;
  const sample = sheets.find(sheet => sheet.name === mapping?.sheet_name);
  const headers = sample?.rows[(mapping?.header_row || 1) - 1]?.map(value => value == null ? '' : String(value).trim()) || [];
  const availableHeaders = headers.filter((header, index) => header && headers.indexOf(header) === index && headers.lastIndexOf(header) === index);
  const updateTemplate = (fields: Partial<TemplateDraft>) => templateDraft && setTemplateDraft({ ...templateDraft, ...fields });
  const changeMapping = (fields: Partial<BankExcelMapping>) => updateTemplate({ mapping: {
    sheet_name: '', header_row: 1, depositor_column: '', amount_column: '', ...(mapping || {}), ...fields,
  }});

  const inspect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file || !templateDraft || disabled || busy) return;
    if (!/\.xlsx?$/i.test(file.name) || file.size > 5 * 1024 * 1024) { setError('5MB 이하의 .xlsx 또는 .xls 파일을 선택해주세요.'); return; }
    setBusy('sample'); setError(''); setMessage('');
    try {
      const form = new FormData(); form.append('file', file);
      const { data } = await api.post(`${SERVER_ADDRESS}store/bank-accounts/inspect`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!Array.isArray(data?.sheets) || !data.sheets.length) throw new Error('Invalid sample');
      setSheets(data.sheets);
      const first = data.sheets[0];
      updateTemplate({ mapping: { sheet_name: first.name, header_row: 1, depositor_column: '', amount_column: '' } });
      setMessage('샘플을 확인하고 입금자명과 입금액 열을 선택해주세요.');
    } catch (cause) { setError(errorText(cause, '엑셀 샘플을 읽지 못했습니다.')); }
    finally { setBusy(null); }
  };

  const saveTemplate = async () => {
    if (!templateDraft || locked) return;
    if (!templateDraft.name.trim() || !mapping?.sheet_name || !mapping.depositor_column || !mapping.amount_column) {
      setError('관리 이름과 입금자명·입금액 열을 모두 입력해주세요.'); return;
    }
    if (mapping.depositor_column === mapping.amount_column) { setError('입금자명과 입금액은 서로 다른 열이어야 합니다.'); return; }
    setBusy('save'); setError('');
    const body = { name: templateDraft.name.trim(), mapping };
    try {
      const { data } = templateDraft.seq
        ? await api.patch(`${SERVER_ADDRESS}store/bank-accounts/excel-templates/${templateDraft.seq}`, body, jsonConfig)
        : await api.post(`${SERVER_ADDRESS}store/bank-accounts/excel-templates`, body, jsonConfig);
      if (linkAccount) {
        if (!Number.isSafeInteger(data?.seq)) throw new Error('Invalid saved template');
        // Keep the saved ID if linking fails so retrying does not create a duplicate template.
        setTemplateDraft({ ...templateDraft, seq: data.seq });
        await patchAccount(linkAccount, { excel_template_seq: data.seq });
      }
      initialDraft.current = ''; setTemplateDraft(null); setLinkAccount(null); setSheets([]); await load(); setMessage('무통장 정산 엑셀을 저장했습니다.');
    } catch (cause) { setError(errorText(cause, '무통장 정산 엑셀을 저장하지 못했습니다.')); }
    finally { setBusy(null); }
  };

  const saveAccount = async () => {
    if (!accountDraft || locked) return;
    if (![accountDraft.label, accountDraft.bank_name, accountDraft.bank_num, accountDraft.depositor].every(value => value.trim())) {
      setError('계좌 이름, 은행명, 계좌번호, 예금주를 모두 입력해주세요.'); return;
    }
    setBusy('save'); setError('');
    const { seq, ...fields } = accountDraft;
    const body = { ...fields, label: fields.label.trim(), bank_name: fields.bank_name.trim(), bank_num: fields.bank_num.trim(), depositor: fields.depositor.trim() };
    try {
      if (seq) await api.patch(`${SERVER_ADDRESS}store/bank-accounts/${seq}`, body, jsonConfig);
      else await api.post(`${SERVER_ADDRESS}store/bank-accounts`, body, jsonConfig);
      initialDraft.current = ''; setAccountDraft(null); await load(); setMessage('통장과 연결 엑셀을 저장했습니다.');
    } catch (cause) { setError(errorText(cause, '통장을 저장하지 못했습니다.')); }
    finally { setBusy(null); }
  };

  const notices = <>{error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}</>;
  const discardNotice = discard && <DiscardNotice role="alert"><p>저장하지 않은 설정이 있습니다. 변경 내용을 버릴까요?</p><Actions>
    <ActionButton type="button" onClick={() => setDiscard(false)}>계속 편집</ActionButton>
    <ActionButton type="button" data-danger onClick={resetEditor}>변경 내용 버리기</ActionButton>
  </Actions></DiscardNotice>;
  const list = <Manager>
    {loading && <p role="status">설정을 불러오는 중입니다.</p>}
    {loadError && <p role="alert">{loadError} <ActionButton type="button" onClick={load}>다시 불러오기</ActionButton></p>}
    {!loading && !loadError && <>
      <ListHeading><h3>등록한 통장 <span>{visibleAccounts.length}개</span></h3>
        <SectionHeading>
          {!selection && <ActionButton type="button" disabled={locked} onClick={() => { setLinkAccount(null); startTemplateDraft(emptyTemplate()); }}>+ 새 엑셀 양식 등록</ActionButton>}
          <ActionButton type="button" data-primary disabled={locked} onClick={() => startAccountDraft(emptyAccount())}>+ 새 통장 등록</ActionButton>
        </SectionHeading></ListHeading>
      {!selection && <Summary>전체 {accounts.length}<span>연결 {accounts.filter(account => account.excel_mapping).length} · 미연결 {accounts.filter(account => !account.excel_mapping).length}</span></Summary>}
      <AccountList aria-label="등록한 통장">{visibleAccounts.slice((currentPage - 1) * 5, currentPage * 5).map(account => {
        const template = templates.find(item => item.seq === account.excel_template_seq);
        const linked = !!account.excel_mapping;
        return <AccountCard key={account.seq} data-selected={!!selection && selectedSeq === account.seq}>
          <AccountCardHeading>
            {selection ? <AccountChoice><input type="radio" name="managed-bank-account" value={account.seq} checked={selectedSeq === account.seq} disabled={disabled || !!busy} onChange={() => setSelectedSeq(account.seq)} /><strong>{account.label}</strong></AccountChoice>
              : <AccountTitle><strong>{account.label}</strong>{!account.active && <AccountStatus>사용 중지</AccountStatus>}</AccountTitle>}
            {!selection && <AccountStatus data-linked={linked}>{linked ? '엑셀 연결됨' : '엑셀 미연결'}</AccountStatus>}
          </AccountCardHeading>
          <AccountDetails><div><dt>은행 / 계좌번호</dt><dd className="account-number">{account.bank_name} {account.bank_num}</dd></div><div><dt>예금주</dt><dd>{account.depositor}</dd></div></AccountDetails>
          <CardActions><TextButton type="button" disabled={locked} onClick={() => startAccountDraft(accountValues(account))}>{selection ? '수정' : '통장 수정'}</TextButton>
            {selection && <TextButton type="button" data-danger disabled={locked} onClick={() => { setError(''); setRemoveAccount(account); }}>삭제</TextButton>}</CardActions>
          {selection ? <AccountMapping>연결 엑셀 · {account.excel_template_name || template?.name || (linked ? '기존 엑셀 양식' : '미연결')}</AccountMapping>
            : <MappingPanel><span>연결된 엑셀 양식</span>{linked ? <>
              <strong>{template?.name || account.excel_template_name || '기존 엑셀 양식'}</strong>
              <p>{account.excel_mapping!.sheet_name} · {account.excel_mapping!.header_row}행부터 · 입금자명 {account.excel_mapping!.depositor_column} · 입금액 {account.excel_mapping!.amount_column}</p>
              <CardActions><TextButton type="button" disabled={locked} onClick={() => startLink(account)}>변경</TextButton>
                <TextButton type="button" disabled={locked} onClick={() => { if (!template) setLinkAccount(account); startTemplateDraft({ seq: template?.seq, name: template?.name || `${account.label} 엑셀`, mapping: { ...account.excel_mapping! } }); }}>수정</TextButton>
                <TextButton type="button" data-danger disabled={locked} onClick={() => updateAccount(account, { excel_template_seq: null })}>삭제</TextButton></CardActions>
            </> : <Unlinked><p>연결된 엑셀 양식이 없습니다.</p><ActionButton type="button" data-primary disabled={locked} onClick={() => startLink(account)}>+ 엑셀 연결</ActionButton></Unlinked>}</MappingPanel>}
        </AccountCard>;
      })}</AccountList>
      {!visibleAccounts.length && <EmptyState><strong>등록한 통장이 없습니다.</strong></EmptyState>}
      {visibleAccounts.length > 5 && <Pagination aria-label="통장 목록 페이지"><ActionButton type="button" disabled={currentPage === 1 || !!busy} onClick={() => setPage(currentPage - 1)}>이전</ActionButton><span>{currentPage} / {pages}</span><ActionButton type="button" disabled={currentPage === pages || !!busy} onClick={() => setPage(currentPage + 1)}>다음</ActionButton></Pagination>}
      {!selection && <p>엑셀 양식을 변경해도 기존 정산 내역은 유지됩니다.</p>}
    </>}
    {!editing && notices}
  </Manager>;

  return <>
    {!selection && <Section aria-label="통장 · 엑셀 관리"><SectionHeading><div><h2>통장 · 엑셀 관리</h2><p>입금 통장과 정산에 사용할 엑셀 양식을 함께 관리하세요.</p></div>
      <ActionButton type="button" aria-haspopup="dialog" disabled={disabled} onClick={() => { setPage(1); setError(''); setMessage(''); setManagerOpen(true); }}>통장 엑셀 관리</ActionButton></SectionHeading></Section>}
    {createPortal(<>
    <LiveDetailDialog open={isOpen} title={selection ? '통장 편집' : '통장 · 엑셀 관리'} description={selection ? '등록한 통장을 관리하고 이번 라이브에 사용할 통장을 선택하세요.' : undefined} size={selection ? 'medium' : 'large'} suspended={editing} onClose={() => { if (!busy) selection ? selection.onClose() : setManagerOpen(false); }}
      footer={<>{selection && <FooterNote>통장 정보는 스토어 설정과 함께 관리됩니다.</FooterNote>}<ActionButton type="button" disabled={!!busy} onClick={() => selection ? selection.onClose() : setManagerOpen(false)}>닫기</ActionButton>{selection && <ActionButton type="button" data-primary disabled={!selectedAccount || loading || !!loadError || !!busy || disabled} onClick={() => selectedAccount && selection.onSelect(selectedAccount)}>선택한 통장 사용</ActionButton>}</>}>{list}</LiveDetailDialog>
    <LiveDetailDialog open={!!accountDraft} onClose={closeEditor} title={accountDraft?.seq ? '통장 수정' : '통장 등록'} size="medium" footer={<><ActionButton type="button" disabled={!!busy} onClick={closeEditor}>취소</ActionButton><ActionButton type="button" data-primary disabled={locked} onClick={saveAccount}>{busy ? '저장 중…' : '저장'}</ActionButton></>}>
      {accountDraft && <Manager><Editor disabled={locked}><Grid>
        <label>계좌 이름<input value={accountDraft.label} maxLength={100} onChange={event => setAccountDraft({ ...accountDraft, label: event.target.value })} /></label>
        <label>은행명<input value={accountDraft.bank_name} maxLength={100} onChange={event => setAccountDraft({ ...accountDraft, bank_name: event.target.value })} /></label>
        <label>계좌번호<input value={accountDraft.bank_num} maxLength={100} inputMode="numeric" onChange={event => setAccountDraft({ ...accountDraft, bank_num: event.target.value })} /></label>
        <label>예금주<input value={accountDraft.depositor} maxLength={100} onChange={event => setAccountDraft({ ...accountDraft, depositor: event.target.value })} /></label>
        <label>연결 통장 엑셀<select aria-label="연결 통장 엑셀" value={accountDraft.excel_template_seq ?? ''} onChange={event => setAccountDraft({ ...accountDraft, excel_template_seq: event.target.value ? Number(event.target.value) : null })}><option value="">연결하지 않음</option>{templates.map(template => <option key={template.seq} value={template.seq}>{template.name}</option>)}</select></label>
      </Grid><AccountUsage><span>계좌 사용</span><UsageOptions><UsageButton type="button" aria-pressed={accountDraft.active} onClick={() => setAccountDraft({ ...accountDraft, active: true })}>사용</UsageButton><UsageButton type="button" aria-pressed={!accountDraft.active} onClick={() => setAccountDraft({ ...accountDraft, active: false })}>사용 중지</UsageButton></UsageOptions></AccountUsage></Editor>{notices}{discardNotice}</Manager>}
    </LiveDetailDialog>
    <LiveDetailDialog open={!!linkAccount && !templateDraft} onClose={() => !busy && setLinkAccount(null)} title="엑셀 연결" size="medium" footer={<><ActionButton type="button" disabled={!!busy} onClick={() => setLinkAccount(null)}>취소</ActionButton><ActionButton type="button" data-primary disabled={locked || !linkTemplate} onClick={() => linkAccount && updateAccount(linkAccount, { excel_template_seq: Number(linkTemplate) })}>연결</ActionButton></>}>
      <Manager><Grid><label>연결할 엑셀 양식<select value={linkTemplate} disabled={locked} onChange={event => setLinkTemplate(event.target.value)}><option value="">양식을 선택해주세요</option>{templates.map(template => <option key={template.seq} value={template.seq}>{template.name}</option>)}</select></label></Grid>
        <Actions><ActionButton type="button" disabled={locked} onClick={() => startTemplateDraft(emptyTemplate())}>새 엑셀 등록</ActionButton></Actions>{notices}</Manager>
    </LiveDetailDialog>
    <LiveDetailDialog open={!!templateDraft} onClose={closeEditor} title={templateDraft?.seq ? '정산 엑셀 수정' : '정산 엑셀 등록'} size="large" footer={<><ActionButton type="button" disabled={!!busy} onClick={closeEditor}>취소</ActionButton><ActionButton type="button" data-primary disabled={locked} onClick={saveTemplate}>{busy ? '저장 중…' : '저장'}</ActionButton></>}>
      {templateDraft && <Manager><Editor disabled={locked}><EditorContent>
        <Grid><label>관리 이름<input value={templateDraft.name} maxLength={100} onChange={event => updateTemplate({ name: event.target.value })} /></label></Grid>
        <ActionButton type="button" onClick={() => sampleInput.current?.click()}>{busy === 'sample' ? '샘플 확인 중…' : '은행 엑셀 샘플 업로드'}</ActionButton>
        <input ref={sampleInput} type="file" aria-label="은행 엑셀 샘플 파일" accept=".xlsx,.xls" hidden onChange={inspect} />
        {mapping && !sheets.length && <p>저장된 양식: {mapping.sheet_name}, {mapping.header_row}행 · 입금자명: {mapping.depositor_column} · 입금액: {mapping.amount_column}</p>}
        {!!sheets.length && mapping && <><Grid>
          <label>엑셀 시트<select value={mapping.sheet_name} onChange={event => changeMapping({ sheet_name: event.target.value, header_row: 1, depositor_column: '', amount_column: '' })}>{sheets.map(sheet => <option key={sheet.name}>{sheet.name}</option>)}</select></label>
          <label>열 제목 행<input type="number" min={1} max={30} value={mapping.header_row} onChange={event => changeMapping({ header_row: Number(event.target.value), depositor_column: '', amount_column: '' })} /></label>
        </Grid><Preview><table><caption>엑셀 샘플 미리보기</caption><tbody>{sample?.rows.slice(0, 30).map((row, index) => <tr key={index} data-header={index + 1 === mapping.header_row}><th>{index + 1}</th>{row.map((cell, column) => <td key={column}>{cell == null ? '' : String(cell)}</td>)}</tr>)}</tbody></table></Preview>
        <Grid>{mappingFields.map(({ key, label }) => <label key={key}>{label}<select value={mapping[key]} onChange={event => changeMapping({ [key]: event.target.value })}><option value="">열 선택</option>{availableHeaders.map(header => <option key={header}>{header}</option>)}</select></label>)}</Grid></>}
      </EditorContent></Editor>{notices}{discardNotice}</Manager>}
    </LiveDetailDialog>
    <LiveDetailDialog open={!!removeAccount} title="통장 삭제" size="compact" onClose={() => !busy && setRemoveAccount(null)} footer={<><ActionButton type="button" disabled={!!busy} onClick={() => setRemoveAccount(null)}>취소</ActionButton><ActionButton type="button" data-danger disabled={locked} onClick={() => removeAccount && updateAccount(removeAccount, { active: false })}>삭제</ActionButton></>}>
      <Manager><p>{removeAccount?.label} 통장을 라이브 선택 목록에서 삭제할까요?</p><p>기존 방송·정산 내역은 유지되며, 스토어 설정에서 다시 사용할 수 있습니다.</p>{error && <p role="alert">{error}</p>}</Manager>
    </LiveDetailDialog>
    </>, document.body)}
  </>;
}

const Section = styled.section`
  margin-top: var(--space-6);
  padding-top: var(--space-6);
  border-top: 1px solid var(--color-border-default);
  h2 { margin: 0; font-size: var(--text-h3); font-weight: var(--font-weight-semibold); }
  p { margin: var(--space-2) 0 0; color: var(--color-text-secondary); font-size: var(--text-body); line-height: var(--leading-body); }
`;
const SectionHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap;
`;
const ActionButton = styled(AdminActionButton)`
  &[data-primary='true'] { background: var(--gradient-brand); color: var(--color-text-on-brand); border-color: var(--color-brand); }
  &[data-primary='true']:hover:not(:disabled) { background: var(--gradient-brand-hover); }
  &[data-danger='true'] { color: var(--color-text-danger); border-color: var(--color-border-danger); }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); border-color: var(--color-border-default); }
`;
const Manager = styled.div`
  color: var(--color-text-primary); font-family: var(--font-family-base);
  h3 { font-size: var(--text-h3); margin: var(--space-5) 0 var(--space-2); }
  p { font-size: var(--text-body); line-height: var(--leading-body); color: var(--color-text-secondary); }
  [role='alert'] { color: var(--color-text-danger); }
  input:focus-visible, select:focus-visible { outline: 2px solid var(--color-text-link); outline-offset: 2px; }
`;
const ListHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap;
  h3 { margin: 0; display: flex; gap: var(--space-2); align-items: center; }
  span { color: var(--color-text-secondary); font-size: var(--text-body); font-weight: var(--font-weight-regular); }
`;
const AccountList = styled.ul`
  display: flex; flex-direction: column; gap: var(--space-3); padding: 0; margin: var(--space-4) 0 0; list-style: none;
`;
const AccountCard = styled.li`
  display: flex; flex-direction: column; gap: var(--space-4); min-width: 0; padding: var(--space-4);
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface);
  &[data-selected='true'] { background: var(--color-brand-subtle); }
  &[data-selected='true'] { border-color: var(--color-brand); }
`;
const AccountCardHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
`;
const AccountTitle = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); min-width: 0; overflow-wrap: anywhere;
  strong { font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); }
`;
const AccountStatus = styled.span`
  flex-shrink: 0; padding: var(--space-1) var(--space-2); border-radius: var(--radius-sm);
  background: var(--color-bg-muted); color: var(--color-text-secondary); font-size: var(--text-caption); font-weight: var(--font-weight-medium);
  &[data-linked='true'] { background: var(--color-brand-subtle); color: var(--color-text-link); }
  &[data-linked='false'] { background: var(--color-warning-subtle); color: var(--color-warning); }
`;
const AccountDetails = styled.dl`
  display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: var(--space-4); margin: 0;
  > div { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  dt { font-size: var(--text-caption); color: var(--color-text-secondary); }
  dd { margin: 0; font-size: var(--text-body); color: var(--color-text-primary); overflow-wrap: anywhere; }
  .account-number { font-variant-numeric: tabular-nums; font-weight: var(--font-weight-medium); }
  @media (max-width: 767px) { grid-template-columns: 1fr; gap: var(--space-3); }
`;
const AccountMapping = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-default); color: var(--color-text-secondary); font-size: var(--text-caption); overflow-wrap: anywhere;
`;
const EmptyState = styled.div`
  margin-top: var(--space-4); padding: var(--space-8) var(--space-4); text-align: center;
  border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-subtle);
`;
const Actions = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: var(--space-2); margin: var(--space-4) 0;`;
const DiscardNotice = styled.div`
  margin-top: var(--space-5); padding: var(--space-4);
  border: 1px solid var(--color-border-danger); border-radius: var(--radius-md); background: var(--color-danger-subtle);
  p { margin: 0; color: var(--color-text-danger); }
  ${Actions} { margin-bottom: 0; }
`;
const Editor = styled.fieldset`
  padding: 0; margin: 0; min-width: 0; border: 0;
  legend { font-weight: var(--font-weight-semibold); padding-right: var(--space-3); }
`;
const EditorContent = styled.div`
  display: flex; flex-direction: column; gap: var(--space-6); padding-top: var(--space-4);
  p, h3 { margin: 0; }
`;
const AccountUsage = styled.div`
  display: flex; flex-direction: column; align-items: flex-start; gap: var(--space-2);
  font-size: var(--text-body);
`;
const UsageOptions = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-2);
`;
const UsageButton = styled(ActionButton)`
  gap: var(--gap-icon);
  svg { width: var(--space-4); height: var(--space-4); flex-shrink: 0; visibility: hidden; }
  &[aria-pressed='true'] { background: var(--color-bg-inverse); color: var(--color-text-on-inverse); border-color: var(--color-bg-inverse); }
  &[aria-pressed='true']:hover:not(:disabled) { background: var(--color-bg-inverse-hover); border-color: var(--color-bg-inverse-hover); }
  &[aria-pressed='true'] svg { visibility: visible; }
  &:disabled { background: var(--color-bg-muted); color: var(--color-text-disabled); border-color: var(--color-border-default); }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-4) var(--space-3);
  label { display: flex; flex-direction: column; gap: var(--space-2); min-width: 0; font-size: var(--text-body); }
  input, select { box-sizing: border-box; width: 100%; min-width: 0; min-height: var(--control-lg); padding: var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); font-size: var(--text-body-lg); }
  @media (max-width: 767px) { grid-template-columns: 1fr; }
`;
const Preview = styled.div`
  overflow: auto; max-height: 260px;
  table { border-collapse: collapse; width: 100%; text-align: left; font-size: var(--text-body); }
  caption { text-align: left; padding-bottom: var(--space-2); }
  th, td { border: 1px solid var(--color-border-table); padding: var(--space-2); min-width: 80px; }
  tr[data-header='true'] { background: var(--color-brand-subtle); font-weight: var(--font-weight-semibold); }
`;

const Summary = styled.div`
  display: flex; justify-content: space-between; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-5);
  color: var(--color-text-secondary); font-size: var(--text-body);
`;
const MappingPanel = styled.div`
  padding: var(--space-4); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-subtle);
  display: flex; flex-direction: column; gap: var(--space-3); overflow-wrap: anywhere;
  > span { color: var(--color-text-secondary); font-size: var(--text-caption); }
  strong { font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); }
  p { margin: 0; }
`;
const CardActions = styled.div`display: flex; justify-content: flex-end; flex-wrap: wrap; gap: var(--space-2);`;
const TextButton = styled(ActionButton)`
  min-height: var(--touch-target); padding: var(--space-1) var(--space-2); background: transparent; border-color: transparent; color: var(--color-text-link);
  &[data-danger='true'] { border-color: transparent; }
`;
const Unlinked = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-3);
`;
const Pagination = styled.nav`display: flex; align-items: center; justify-content: center; gap: var(--space-4); margin: var(--space-5) 0;`;
const FooterNote = styled.span`flex: 1; min-width: 0; color: var(--color-text-secondary); font-size: var(--text-caption);`;
const AccountChoice = styled.label`
  display: flex; align-items: center; gap: var(--space-3); cursor: pointer; min-height: var(--touch-target); min-width: 0;
  input { accent-color: var(--color-brand); flex-shrink: 0; }
  strong { font-size: var(--text-body-lg); font-weight: var(--font-weight-semibold); overflow-wrap: anywhere; }
`;
